import React from 'react';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import { Button, Icon, InputField } from '#ui-kit';
import { ButtonSize, InputTypes, TypographyTag } from '#ui-kit.types';
import { FormContainer, FormSection } from '#components';
import {
  DeliverySettingInputField,
  DeliverySettingTitle,
  DeliveryStepContainer,
  DeliveryStepRemoveBtn,
} from './style';
import { RegionFormInitialValues, RegionFormProps } from './types';
import IconsNames from 'components/ui-kit/Icons/types/Icons.names';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Поле обязательно для заполнения'),
  lat: Yup.number()
    .typeError('Значение должно быть числом')
    .required('Поле обязательно для заполнения')
    .min(-90, 'Широта должна быть от -90 до 90')
    .max(90, 'Широта должна быть от -90 до 90'),
  lng: Yup.number()
    .typeError('Значение должно быть числом')
    .required('Поле обязательно для заполнения')
    .min(-180, 'Долгота должна быть от -180 до 180')
    .max(180, 'Долгота должна быть от -180 до 180'),
  settings: Yup.object().shape({
    deliveryPriceSteps: Yup.array()
      .min(1, 'Поле обязательно для заполнения')
      .of(
        Yup.object().shape({
          minPrice: Yup.number()
            .typeError('Значение должно быть числом')
            .required('Поле обязательно для заполнения')
            .min(0, 'Минимальная сумма не может быть отрицательной')
            .transform((value) => Number(value)),
          deliveryPrice: Yup.number()
            .typeError('Значение должно быть числом')
            .required('Поле обязательно для заполнения')
            .min(0, 'Стоимость доставки не может быть отрицательной')
            .transform((value) => Number(value)),
        }),
      )
      .test(
        'minPrice-order',
        'Минимальная сумма каждого этапа должна быть больше предыдущего',
        function (steps) {
          if (!steps || steps.length < 2) return true;

          for (let i = 1; i < steps.length; i++) {
            if (steps[i].minPrice <= steps[i - 1].minPrice) {
              return this.createError({
                path: `settings.deliveryPriceSteps[${i}].minPrice`,
                message:
                  'Минимальная сумма должна быть больше предыдущего этапа',
              });
            }
          }

          return true;
        },
      ),
  }),
});

const RegionForm: React.FC<RegionFormProps> = ({ data, loading, onSubmit }) => {
  const initialValues: RegionFormInitialValues = {
    name: data?.name ?? '',
    settings: data?.settings ?? {
      deliveryPriceSteps: [
        {
          minPrice: 0,
          deliveryPrice: 0,
        },
      ],
    },
    createdAt: data?.createdAt ?? '',
    lat: data?.lat ?? 0,
    lng: data?.lng ?? 0,
  };

  const handleSubmit = async (values: RegionFormInitialValues) => {
    const requestShouldContinue = data
      ? confirm('Вы действительно хотите изменить регион?')
      : confirm('Вы действительно хотите создать регион?');

    const result = {
      ...values,
    };

    result.lat = Number(result.lat);
    result.lng = Number(result.lng);
    result.settings.deliveryPriceSteps = result.settings.deliveryPriceSteps.map(
      (step) => ({
        minPrice: Number(step.minPrice),
        deliveryPrice: Number(step.deliveryPrice),
      }),
    );

    if (requestShouldContinue) {
      await onSubmit(result);
    }
  };

  return (
    <>
      <Formik
        {...{
          initialValues,
          validationSchema,
          onSubmit: handleSubmit,
        }}
      >
        {({ values, errors, touched, dirty }) => (
          <FormContainer
            leftSideActions={
              <Button label="Сохранить" loading={loading} disabled={!dirty} />
            }
          >
            <FormSection
              {...{
                label: 'Наименование',
              }}
            >
              <InputField
                name="name"
                placeholder="Введите наименование"
                clearable
                disabled={loading}
              />
            </FormSection>
            <FormSection
              {...{
                label: 'Долгота',
              }}
            >
              <InputField name="lng" placeholder="Введите долготу" clearable />
            </FormSection>
            <FormSection
              {...{
                label: 'Широта',
              }}
            >
              <InputField name="lat" placeholder="Введите широту" clearable />
            </FormSection>
            <FormSection label="Этапы стоимости доставки">
              <FieldArray name="settings.deliveryPriceSteps">
                {({ push, remove }) => (
                  <>
                    {values.settings.deliveryPriceSteps.map((step, idx) => {
                      const minPriceTitle =
                        idx === 0
                          ? 'Мин. сумма для доставки'
                          : 'Мин. сумма для этапа';

                      return (
                        <DeliveryStepContainer key={idx}>
                          <DeliverySettingTitle tag={TypographyTag.p}>
                            {idx === 0
                              ? 'Минимальные требования для доставки'
                              : `Этап ${idx}`}
                          </DeliverySettingTitle>
                          <DeliverySettingInputField
                            name={`settings.deliveryPriceSteps[${idx}].minPrice`}
                            placeholder={minPriceTitle}
                            label={minPriceTitle}
                            type={InputTypes.Number}
                            error={
                              touched.settings?.deliveryPriceSteps?.[idx]
                                ?.minPrice &&
                              (
                                errors.settings?.deliveryPriceSteps?.[
                                  idx
                                ] as any
                              )?.minPrice
                            }
                          />
                          <DeliverySettingInputField
                            name={`settings.deliveryPriceSteps[${idx}].deliveryPrice`}
                            placeholder="Стоимость доставки"
                            label="Стоимость доставки"
                            type={InputTypes.Number}
                            error={
                              touched.settings?.deliveryPriceSteps?.[idx]
                                ?.deliveryPrice &&
                              (
                                errors.settings?.deliveryPriceSteps?.[
                                  idx
                                ] as any
                              )?.deliveryPrice
                            }
                          />
                          {values.settings.deliveryPriceSteps.length > 1 &&
                            idx !== 0 && (
                              <DeliveryStepRemoveBtn
                                type="button"
                                onClick={() => remove(idx)}
                              >
                                <Icon name={IconsNames.cross} />
                              </DeliveryStepRemoveBtn>
                            )}
                        </DeliveryStepContainer>
                      );
                    })}
                    <Button
                      type="button"
                      label="Добавить этап стоимости доставки"
                      onClick={() => push({ minPrice: 0, deliveryPrice: 0 })}
                      size={ButtonSize.Small}
                    />
                  </>
                )}
              </FieldArray>
            </FormSection>
          </FormContainer>
        )}
      </Formik>
    </>
  );
};

export default RegionForm;
